import Cookies from 'js-cookie';
import { useState, useEffect } from 'react';

import { HitProps, HitVariants } from '@components/algolia/Hit';
import { formatPrice } from '@hooks/usePrice';
import useCustomer from '@adapters/bigcommerce/hooks/customer/useCustomer';
import { getGroupKeyByCustomerGroupId } from '@utils/selectPriceByGroup';

export const buildPrice = (
  price: HitProps['price'],
  salePrice: HitProps['salePrice'],
  priceRange?: { price?: [number, number]; salePrice?: [number, number] },
  locale = 'en'
) => {
  let priceFormatted;
  let salePriceFormatted;
  let salePriceRange;
  let normalPriceRange;
  if (price > 0) {
    priceFormatted = formatPrice({
      amount: price,
      currencyCode: 'CAD',
      locale,
    });
  }
  if (salePrice > 0) {
    salePriceFormatted = formatPrice({
      amount: salePrice,
      currencyCode: 'CAD',
      locale,
    });
  }
  if (priceRange?.price) {
    normalPriceRange = `${formatPrice({
      amount: priceRange.price[0],
      currencyCode: 'CAD',
      locale,
    })} - ${formatPrice({
      amount: priceRange.price[1],
      currencyCode: 'CAD',
      locale,
    })}`;
  }
  if (priceRange?.salePrice) {
    salePriceRange = `${formatPrice({
      amount: priceRange.salePrice[0],
      currencyCode: 'CAD',
      locale,
    })} - ${formatPrice({
      amount: priceRange.salePrice[1],
      currencyCode: 'CAD',
      locale,
    })}`;
  }
  return {
    priceFormatted,
    salePriceFormatted,
    normalPriceRange,
    salePriceRange,
  };
};

export const useHitPrice = (
  hitColour: string,
  variants: HitVariants,
  locale?: string
) => {
  const algoliaDebug = Cookies.get('algoliaDebug');
  const [hasMounted, setHasMounted] = useState(false);
  const { customer } = useCustomer();

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const priceGroupKey =
    hasMounted && customer?.customerGroupId
      ? getGroupKeyByCustomerGroupId(customer.customerGroupId)
      : 'guest';

  const { price, salePrice, priceRange, prices } = variants[hitColour];

  // `salePrice` falls back to the legacy top-level field only when this
  // group has no entry at all in `prices` — a present entry missing
  // `salePrice` means that group legitimately has no sale (0), and must not
  // inherit another group's legacy sale price. `price`/`priceRange` don't
  // carry that ambiguity (a present group entry always has its own price),
  // so they keep the simple per-field fallback.
  const selectedPrice = prices?.[priceGroupKey]?.price ?? price;
  const selectedSalePrice = prices?.[priceGroupKey]
    ? prices[priceGroupKey]?.salePrice ?? 0
    : salePrice;
  const selectedPriceRange = prices?.[priceGroupKey]?.priceRange ?? priceRange;

  const guestPrice = prices?.guest?.price ?? price;
  const guestSalePrice = prices?.guest
    ? prices.guest.salePrice ?? 0
    : salePrice;
  const guestPriceRangeData = prices?.guest?.priceRange ?? priceRange;

  const formattedPrices = buildPrice(
    selectedPrice,
    selectedSalePrice,
    selectedPriceRange,
    locale
  );

  let guestPriceFormatted = formattedPrices.priceFormatted;
  let guestNormalPriceRange = formattedPrices.normalPriceRange;

  if (hasMounted && priceGroupKey !== 'guest') {
    const guestFormatted = buildPrice(
      guestPrice,
      guestSalePrice,
      guestPriceRangeData,
      locale
    );
    guestPriceFormatted = guestFormatted.priceFormatted;
    guestNormalPriceRange = guestFormatted.normalPriceRange;
  }

  // Compares effective (sale-inclusive) prices, not just base price — a group can be
  // genuinely discounted via `salePrice` alone while its base `price` is unchanged.
  const isGuestPriceGreater = (() => {
    const guestEffectivePrice =
      guestSalePrice > 0 ? guestSalePrice : guestPrice;
    const selectedEffectivePrice =
      selectedSalePrice > 0 ? selectedSalePrice : selectedPrice;
    if (guestEffectivePrice != null && selectedEffectivePrice != null) {
      return guestEffectivePrice > selectedEffectivePrice;
    }
    const guestEffectiveRangeLow =
      guestPriceRangeData?.salePrice?.[0] ?? guestPriceRangeData?.price?.[0];
    const selectedEffectiveRangeLow =
      selectedPriceRange?.salePrice?.[0] ?? selectedPriceRange?.price?.[0];
    if (guestEffectiveRangeLow != null && selectedEffectiveRangeLow != null) {
      return guestEffectiveRangeLow > selectedEffectiveRangeLow;
    }
    return false;
  })();

  const shouldShowGuestPriceComparison =
    hasMounted && priceGroupKey !== 'guest' && isGuestPriceGreater;

  if (algoliaDebug) {
    console.log('useHitPrice', {
      ...formattedPrices,
      price: selectedPrice,
      salePrice: selectedSalePrice,
      priceRange: selectedPriceRange,
      priceGroupKey,
      guestPriceFormatted,
      guestNormalPriceRange,
      shouldShowGuestPriceComparison,
    });
  }

  return {
    ...formattedPrices,
    price: selectedPrice,
    salePrice: selectedSalePrice,
    priceRange: selectedPriceRange,
    guestPriceFormatted,
    guestNormalPriceRange,
    shouldShowGuestPriceComparison,
  };
};
